import React, { useMemo, useState } from 'react'
import {
  DomainTag,
  useGetDomainTagListQuery,
  useLazyGetDomainTagByDomainQuery,
  usePostDomainTagAssignMutation,
  usePostDomainTagCreateMutation,
  usePostDomainTagDeleteMutation,
  usePostDomainTagUnassignMutation,
  usePostDomainTagUpdateMutation,
} from '../../../app/api'
import { BpInput } from '../../../shared/components/forms/input/BpInput'
import { IconSearch } from '../../../shared/components/icons/IconSearch'
import { Input, Tooltip } from 'antd'
import { IconPlus } from '../../../shared/components/icons/IconPlus'
import { IconArrowBack } from '../../../shared/components/icons/IconArrowBack'
import { BpButton } from '../../../shared/components/buttons/bp-button/BpButton'
import { IconTrash } from '../../../shared/components/icons/IconTrash'
import { IconButton } from '../../../shared/components/buttons/icon-button/IconButton'
import { Tag } from './ui/Tag'
import { useTranslation } from 'react-i18next'
import { IWithDropdownProps, withDropdown } from '../../../shared/lib/hoc/WithDropdown'
import { IconDotsVertical } from '../../../shared/components/icons/IconDotsVertical'
import { IconSuccess } from '../../../shared/components/icons/IconSuccess'
import { useAppDispatch } from '../../../app/store/hooks/Redux'
import { hideModal, setModal } from '../../../app/store/reducers/AppSlice'
import { ConfirmModal } from './ui/ConfirmModal'
import { DropdownPositioner } from '../../ui/dropdown-positioner/component/DropdownPositioner'
import { BADGE_COLOR, TBadgeColorKey } from '../../ui/badge/const/badge-color.const'
import { TAG_COLORS } from './const/tag-colors.const'

export interface CustomTag extends Omit<DomainTag, 'id' | 'color'> {
  id: number
  color: (typeof TAG_COLORS)[number]
}

interface TagsEditorProps extends IWithDropdownProps {
  domain: string
  project: string
  tags: (string | CustomTag)[]
  onChange?: () => void
  onAssign?: () => void
  onUnassign?: (id: number) => void
  onDelete?: (tag: CustomTag) => void
}

const tagsCache = new Map<string, (string | CustomTag)[]>()

const Drop = ({
  project,
  domain,
  tags: initialTags,
  open,
  onToggle,
  ...props
}: TagsEditorProps) => {
  const { onChange, onAssign, onUnassign, onDelete } = props

  const { t } = useTranslation()
  const cacheKey = `${project}:${domain}`

  const [tags, setTags] = useState(() => tagsCache.get(cacheKey) ?? initialTags)

  const updateTags = (list: (string | CustomTag)[]) => {
    tagsCache.set(cacheKey, list)
    setTags(list)
  }
  const [search, setSearch] = useState('')
  const [selectedTagInitial, setSelectedTagInitial] = useState<CustomTag | null>(null)
  const [selectedTagControl, setSelectedTagControl] = useState<CustomTag | null>(null)
  const dispatch = useAppDispatch()

  const { data } = useGetDomainTagListQuery({ projectId: Number(project) })
  const [getTagList, { isFetching: listIsFetching }] = useLazyGetDomainTagByDomainQuery()
  const [createTag] = usePostDomainTagCreateMutation()
  const [updateTag] = usePostDomainTagUpdateMutation()
  const [assignTag, { isLoading: assignIsLoading }] = usePostDomainTagAssignMutation()
  const [unassignTag, { isLoading: unassignIsLoading }] = usePostDomainTagUnassignMutation()
  const [deleteTag] = usePostDomainTagDeleteMutation()

  const clearSelectedTag = () => {
    setSelectedTagControl(null)
    setSelectedTagInitial(null)
  }

  const selectTagForEdit = (tag: CustomTag) => {
    setSelectedTagControl({ ...tag })
    setSelectedTagInitial({ ...tag })
  }

  const isSelectedTagDirty =
    !!selectedTagControl &&
    !!selectedTagInitial &&
    (selectedTagControl.name !== selectedTagInitial.name ||
      selectedTagControl.color !== selectedTagInitial.color)

  const handleCreateTag = () => {
    createTag({ body: { project_id: Number(project), name: search } })
      .unwrap()
      .then((response) => {
        const tag = response.data?.items?.[0] as CustomTag | undefined

        if (response.success && tag) handleAssignTag(tag.id)
      })
      .catch(() => undefined)
      .finally(() => setSearch(''))
  }

  const handleUpdateTag = () => {
    if (selectedTagControl)
      dispatch(
        setModal({
          size: 'md',
          children: (
            <ConfirmModal
              title={t('components.tagsEditor.confirmEdit')}
              onConfirm={() => {
                dispatch(hideModal())
                updateTag({
                  body: {
                    id: selectedTagControl.id,
                    project_id: Number(project),
                    name: selectedTagControl?.name,
                    color: selectedTagControl?.color,
                  },
                }).then(() => clearSelectedTag())
              }}
              onCancel={() => clearSelectedTag()}
            >
              {t('components.tagsEditor.willAffectAll')}
            </ConfirmModal>
          ),
        }),
      )
  }

  const handleAssignTag = (id: number) => {
    const isAssigned = tags.some((tag) => typeof tag !== 'string' && tag.id === id)

    if (isAssigned) {
      handleUnassignTag(id)
    } else {
      assignTag({ body: { domain_tag_id: id, domain, project_id: Number(project) } }).then(() => {
        handleUpdateList()
        onAssign && onAssign()
      })
    }

    onToggle && onToggle()
    onChange && onChange()
  }

  const handleUnassignTag = (id: number) => {
    unassignTag({ body: { domain_tag_id: id, domain, project_id: Number(project) } }).then(() => {
      handleUpdateList()
      onUnassign && onUnassign(id)
    })
  }

  const handleUpdateList = () => {
    getTagList({ projectId: Number(project), domain }).then((response: any) => {
      const systemTags = initialTags.filter((tag) => typeof tag === 'string')
      const list = response?.data?.data?.items

      if (list) {
        updateTags([...systemTags, ...list])
      }
    })
  }

  const handleDeleteTag = () => {
    if (selectedTagControl)
      dispatch(
        setModal({
          size: 'md',
          children: (
            <ConfirmModal
              title={t('components.tagsEditor.confirmDelete')}
              onDelete={() => {
                dispatch(hideModal())
                deleteTag({
                  body: { id: selectedTagControl.id, project_id: Number(project) },
                }).then(() => {
                  clearSelectedTag()
                  onDelete && onDelete(selectedTagControl)
                })
              }}
              onCancel={() => clearSelectedTag()}
            >
              {t('components.tagsEditor.willAffectAll')}
            </ConfirmModal>
          ),
        }),
      )
  }

  const tagItems = (data?.data?.items ?? []) as unknown as CustomTag[]

  const syncedTags = useMemo(() => {
    const byId = new Map(tagItems.map((tag) => [tag.id, tag]))

    return tags
      .map((tag) => (typeof tag === 'string' ? tag : (byId.get(tag.id) ?? tag)))
      .filter((tag) => typeof tag === 'string' || byId.has(tag.id))
  }, [tags, tagItems])

  const croppedTags = syncedTags.slice(0, 6)

  const chunkedTags = useMemo(() => {
    const chunks = []

    for (let i = 0; i < croppedTags.length; i += 3) {
      chunks.push(croppedTags.slice(i, i + 3))
    }

    return chunks
  }, [croppedTags])

  const hiddenTags = syncedTags.slice(6)

  const filteredTagItems = useMemo(() => {
    const query = search.trim().toLowerCase()

    if (!query) return tagItems

    return tagItems.filter((tag: CustomTag) => tag.name.toLowerCase().includes(query))
  }, [tagItems, search])

  const searchMatchesExistingTag = useMemo(() => {
    const query = search.trim().toLowerCase()

    if (!query) return false

    return tagItems.some((tag: CustomTag) => tag.name.trim().toLowerCase() === query)
  }, [tagItems, search])

  return (
    <div className={'relative table w-full select-none'}>
      <div className={'flex flex-col gap-[4px]'}>
        {chunkedTags.map((chunk, chunkIndex) => (
          <div key={chunkIndex} className={'flex gap-[4px]'}>
            {chunk.map((tag, tagIndex) => {
              const isCustom = typeof tag !== 'string'
              const isLast = chunkedTags.length - 1 === chunkIndex && chunk.length - 1 === tagIndex

              return (
                <div key={isCustom ? tag.name : tag} className={'flex items-center gap-[4px]'}>
                  <Tag
                    color={isCustom ? tag.color : undefined}
                    id={isCustom ? tag.id : undefined}
                    maw={isCustom ? 80 : undefined}
                    onClear={handleUnassignTag}
                  >
                    {isCustom ? tag.name : t('components.tags.' + tag)}
                  </Tag>
                  {isLast && (
                    <div className={'flex items-center gap-[4px]'}>
                      {!!hiddenTags.length && (
                        <Tooltip
                          title={hiddenTags
                            .map((tag) => {
                              const isCustom = typeof tag !== 'string'

                              return isCustom ? tag.name : t('components.tags.' + tag)
                            })
                            .join(', ')}
                        >
                          <span
                            className={
                              'ml-auto cursor-default rounded-[4px] bg-focus-50 px-[4px] text-[12px] leading-[16px] text-aqua-300'
                            }
                          >
                            +{hiddenTags.length}
                          </span>
                        </Tooltip>
                      )}
                      <button
                        className={
                          'row-hover-visible flex w-fit cursor-pointer items-center gap-[4px] whitespace-nowrap text-[12px] leading-[16px] text-focus'
                        }
                        onClick={onToggle}
                      >
                        <IconPlus className={'w-[10px]'} color={'#0E63D8'} />
                        {t('components.tagsEditor.addTag')}
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ))}
        {!tags.length && (
          <button
            className={
              'row-hover-visible flex w-fit cursor-pointer items-center gap-[4px] whitespace-nowrap text-[12px] leading-[16px] text-focus'
            }
            onClick={onToggle}
          >
            <IconPlus className={'w-[10px]'} color={'#0E63D8'} />
            {t('components.tagsEditor.addTag')}
          </button>
        )}
      </div>
      <DropdownPositioner open={open} position='auto' width={250}>
        <div className='flex flex-col overflow-hidden rounded-[6px] bg-white shadow-outline shadow-default'>
          {selectedTagControl ? (
            <div className={'flex flex-col gap-[12px] p-[8px]'}>
              <div className={''}>
                <BpButton
                  iconLeft={<IconArrowBack />}
                  size='sm'
                  theme='transparent'
                  onClick={clearSelectedTag}
                >
                  {t('buttons.back')}
                </BpButton>
              </div>
              <div>
                <BpInput
                  placeholder={t('components.tagsEditor.tagName')}
                  value={selectedTagControl.name}
                  onChange={(e) =>
                    setSelectedTagControl({ ...selectedTagControl, name: e.target.value })
                  }
                />
              </div>
              <div className={'flex gap-[4px]'}>
                {TAG_COLORS.map((key, index) => (
                  <button
                    key={index}
                    className={'flex h-[32px] w-full items-center justify-center rounded-[8px]'}
                    style={{ backgroundColor: BADGE_COLOR[key].color }}
                    onClick={() => setSelectedTagControl({ ...selectedTagControl, color: key })}
                  >
                    {selectedTagControl.color === key && (
                      <span className={'block w-[18px]'}>
                        <IconSuccess />
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div className={'flex items-center justify-between'}>
                <BpButton
                  disabled={!isSelectedTagDirty || !selectedTagControl.name.length}
                  size={'sm'}
                  onClick={handleUpdateTag}
                >
                  {t('buttons.save')}
                </BpButton>
                <IconButton size='lg' onClick={handleDeleteTag}>
                  <IconTrash color='#00326D' />
                </IconButton>
              </div>
            </div>
          ) : (
            <div className={'flex min-h-0 flex-col'}>
              <div
                className={'flex items-center border-b border-default/[0.07] py-[4px] px-[12px]'}
              >
                <div className={'w-[14px]'}>
                  <IconSearch color={'#00326D'} />
                </div>
                <Input
                  className={
                    '!border-none text-[14px] text-focus-500 !outline-none transition-none focus:shadow-none'
                  }
                  placeholder={t('fields.searchOrCreateTag')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button
                  className={`w-[14px] ${(!search.trim().length || searchMatchesExistingTag) && 'pointer-events-none invisible'}`}
                  type='button'
                  onClick={handleCreateTag}
                >
                  <IconPlus color={'#00326D'} />
                </button>
              </div>
              <div className={'flex max-h-[200px] flex-col overflow-auto'}>
                {filteredTagItems.length ? (
                  filteredTagItems.map((tag: CustomTag) => {
                    const { id, name, color } = tag
                    const isAssigned = tags.some((tag) => typeof tag !== 'string' && tag.id === id)

                    return (
                      <div
                        key={id}
                        className={`group flex items-center justify-between gap-[4px] px-[12px] py-[8px] ${isAssigned ? 'bg-default/[0.075]' : 'hover:bg-default-200'}`}
                      >
                        <button
                          className='min-w-0 flex-1 text-left disabled:opacity-80'
                          disabled={assignIsLoading || unassignIsLoading || listIsFetching}
                          type='button'
                          onClick={() => handleAssignTag(id)}
                        >
                          <Tag color={color}>{name}</Tag>
                        </button>
                        <button
                          className={
                            'pointer-events-none min-w-[24px] max-w-[24px] shrink-0 rounded-[4px] p-[4px] opacity-0 transition-opacity hover:bg-aqua/[.1] focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100'
                          }
                          type='button'
                          onClick={() => selectTagForEdit(tag)}
                        >
                          <IconDotsVertical color={'#6684a7'} />
                        </button>
                      </div>
                    )
                  })
                ) : tagItems.length ? (
                  <div className='py-[32px] px-[8px] text-center text-[14px] text-aqua-300'>
                    {t('components.tagsEditor.noMatchingTags')}
                  </div>
                ) : (
                  <div className='py-[32px] px-[8px] text-center text-[14px] text-aqua-300'>
                    {t('components.tagsEditor.createTagHint')}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DropdownPositioner>
    </div>
  )
}

export const TagsEditor = withDropdown(Drop)
